<script lang="ts">
    import {Link} from "svelte-navigator";
    import {
        CopyIcon,
        FeatherShare2,
        TrashIcon,
    } from "@anyways-open/common-svgs";
    import {_} from "svelte-i18n";
    import "status-indicator/styles.css";

    import {appManager} from "../../AppManager";
    import {createEventDispatcher} from "svelte";
    import type {ReRouteData} from "../../api/projects/data/reroute/ReRouteData";
    import type {ProjectAndScenariosData} from "../../api/projects/data/projects/ProjectAndScenariosData";

    const dispatch = createEventDispatcher();

    const emptyScenarioId = "NONE";

    export let reroute: ReRouteData;
    export let project: ProjectAndScenariosData;
    reroute.name ??= "";
    reroute.description ??= "";

    let name: string = reroute.name;
    let description: string = reroute.description;
    let scenario1: string = reroute.scenarios[0]?.scenario?.id ?? emptyScenarioId;
    ;
    let scenario2: string = reroute.scenarios[1]?.scenario?.id ?? emptyScenarioId;
    ;
    let published: boolean = reroute.published;
    let scenarios: { id: string; description: string }[] = [];
    let url: string = `${appManager.PublicUrl}routeplanner/${reroute.id}`;

    $: if (typeof reroute !== "undefined") {
        updateReRoute();
    }

    $: if (typeof project !== "undefined") {
        scenarios = [];

        project.scenarios.forEach((s) => {
            scenarios.push({
                id: s.id,
                description: `${s.name ?? ""}`,
            });
        });
    } else {
        scenarios = [];
    }

    const updateReRoute = () => {
        reroute.name ??= "";
        name = reroute.name;
        description = reroute.description;
        scenario1 = reroute.scenarios[0]?.scenario?.id ?? emptyScenarioId;
        scenario2 = reroute.scenarios[1]?.scenario?.id ?? emptyScenarioId;
        published = reroute.published;
        url = `${appManager.PublicUrl}routeplanner/${reroute.id}`;
    };

    async function update() {
        if (typeof reroute !== "undefined" && (
            reroute.name !== name || reroute.description !== description)) {
            reroute.name = name;
            reroute.description = description;

            await appManager.api.projects.updateReroute({
                accessToken:
                    await appManager.api.getAccessToken(),
                id: reroute.id,
                data: {
                    name: name,
                    description: description,
                },
            });
        }
    }

    $: if (typeof scenario1 !== "undefined" && typeof scenario2 !== "undefined") {
        updateScenarios().then();
    }

    const updateScenarios = async () => {
        if (scenario1 === (reroute.scenarios[0]?.scenario?.id ?? emptyScenarioId) &&
            scenario2 === (reroute.scenarios[1]?.scenario?.id ?? emptyScenarioId)) return;

        const scenarios = [];
        if (scenario1 !== emptyScenarioId) scenarios.push(scenario1);
        if (scenario2 !== emptyScenarioId) scenarios.push(scenario2);

        await appManager.api.projects.updateReroute({
            accessToken:
                await appManager.api.getAccessToken(),
            id: reroute.id,
            data: {
                scenarios: scenarios,
            },
        });

        if (scenario1 == emptyScenarioId) {
            reroute.scenarios[0] = {
                name: "",
                scenario: undefined
            };
        } else {
            reroute.scenarios[0] = {
                name: "",
                scenario: project.scenarios.find(x => x.id == scenario1)
            };
        }
        if (scenario2 == emptyScenarioId) {
            reroute.scenarios[1] = {
                name: "",
                scenario: undefined
            };
        } else {
            reroute.scenarios[1] = {
                name: "",
                scenario: project.scenarios.find(x => x.id == scenario2)
            };
        }

        dispatch("scenarios");
    };

    const onPublish = async () => {
        published = true;

        await appManager.api.projects.updateReroute({
            accessToken:
                await appManager.api.getAccessToken(),
            id: reroute.id,
            data: {
                published: published,
            },
        });

        reroute.published = published;

        dispatch("publish");
    };

    const onPrivate = async () => {
        published = false;

        await appManager.api.projects.updateReroute({
            accessToken:
                await appManager.api.getAccessToken(),
            id: reroute.id,
            data: {
                published: published,
            },
        });

        reroute.published = published;

        dispatch("publish");
    };

    const onRemove = async () => {
        const confirmed = await appManager.ModalHooks.DeleteConfirmModal.open(
            $_("impact.project.routeplanner.confirmdelete")
        );

        if (confirmed) {
            await appManager.api.projects.deleteReroute({
                accessToken:
                    await appManager.api.getAccessToken(),
                id: reroute.id,
            });

            dispatch("removed");
        }
    };
</script>

<div class="list-group-item">
    <div class="row align-items-center alert alert-dark px-0">
        <div class="col-9">
            {#if !published}
                <div class="px-3 d-inline">
                    <a href={url} target="_blank" rel="noreferrer"
                    >
                        <status-indicator negative/>
                    </a>
                </div>
                {$_("impact.project.routeplanner.private.part1")}<a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
            >{$_("impact.project.routeplanner.private.part2")}</a
            >{$_("impact.project.routeplanner.private.part3")}
            {:else}
                <div class="px-3 d-inline">
                    <a href={url} target="_blank" rel="noreferrer"
                    >
                        <status-indicator positive pulse/>
                    </a>
                </div>
                {$_("impact.project.routeplanner.public.part1")}<a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
            >{$_("impact.project.routeplanner.public.part2")}</a>
            {/if}
        </div>
        <div class="ms-auto col-auto">
            {#if !published}
                <button class="btn btn-xs btn-primary me-1" on:click={onPublish}
                >{$_("impact.project.routeplanner.makepublic")}</button>
            {:else}
                <button class="btn btn-xs btn-primary me-1" on:click={onPrivate}
                >{$_("impact.project.routeplanner.makeprivate")}</button>
            {/if}
        </div>
    </div>

    <div class="row align-items-center mt-4">
        <div class="col-12">
            <div class="form-floating">
                <div
                        id="name"
                        class="form-control"
                        on:focusout={async () => await update()}
                        contenteditable="true"
                        bind:innerHTML={name}/>
                <label for="name">
                    {$_("impact.project.routeplanner.name")}</label>
            </div>
        </div>
    <div class="row align-items-center mt-4">
    </div>
        <div class="col-12">
            <div class="form-floating">
                <div
                        id="name"
                        class="form-control"
                        on:focusout={async () => await update()}
                        contenteditable="true"
                        bind:innerHTML={description}/>
                <label for="name">
                    {$_("impact.project.routeplanner.description")}</label>
            </div>
        </div>
    </div>
    <div class="row align-items-center mt-4">
        <div class="col-6">
            <div class="form-floating">
                <select
                        bind:value={scenario1}
                        class="form-select"
                        id="scenario1">
                    <option value={emptyScenarioId}>{$_("impact.project.routeplanner.noscenario")}</option>
                    {#each scenarios as o}
                        <option value={o.id}>{@html o.description}</option>
                    {/each}
                </select>
                <label for="scenario1">
                    {$_("impact.project.routeplanner.scenario1")}</label>
            </div>
        </div>
        <div class="col-6">
            <div class="form-floating">
                <select
                        bind:value={scenario2}
                        class="form-select"
                        id="scenario2">
                    <option value={emptyScenarioId}>{$_("impact.project.routeplanner.noscenario")}</option>
                    {#each scenarios as o}
                        <option value={o.id}>{@html o.description}</option>
                    {/each}
                </select>
                <label for="scenario2">
                    {$_("impact.project.routeplanner.scenario2")}</label>
            </div>
        </div>
    </div>
    <div class="row align-items-center mt-4">
        <div class="ms-auto col-auto">
            {#if published}
                <span title={$_("impact.project.routeplanner.deletepublished")}>
                    <button class="btn btn-xs btn-danger me-1 disabled"
                    >{$_("impact.project.routeplanner.delete")}</button>
                </span>
            {:else}
                <button class="btn btn-xs btn-danger me-1" on:click={onRemove}
                >{$_("impact.project.routeplanner.delete")}</button>
            {/if}
        </div>
    </div>
</div>

<style>
    /* .row {
        border-bottom: rgb(226, 226, 226) 1px solid;
    }
    .row:last-child {
        border-bottom: none;
    } */

    .status-light {
        /* Center the content */
        align-items: center;
        display: flex;
    }

    .status-light__status {
        /* Background color */
        background-color: #16a34a;

        /* Rounded border */
        border-radius: 9999px;

        /* Size */
        height: 0.5rem;
        width: 0.5rem;

        /* Spacing */
        margin-right: 0.5rem;
    }

    .status-light__content {
        /* Take available width */
        flex: 1;
    }

    .badge-scenario {
        background-color: #ffc926;
        color: #000;
        height: 20px;
        margin: 2px;
    }

    .badge-hover:hover {
        box-shadow: 1px 1px 5px gray;
    }

    .btn.disabled.icon {
        opacity: 0.2;
    }
</style>
